/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarNavList';

const SidebarNavListManager: ComponentManagerFn<'SidebarNavList'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarNavList'>(platform, 'SidebarNavList'),
    type: 'SidebarNavList',
    name: 'Sidebar Nav List',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnClick',
          'IconButton',
          'SidebarNavLink',
          'SidebarNavDropdown',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore', 'DataStore'],
      },
      icon: {
        category: 'Look & Feel',
        label: 'Icon Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.alias ?? '',
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    category: 'Sidebar',
    canHaveChildren: () => true,
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Sidebar Nav List is a data-driven navigation component for Sidebar Panels. Unlike the static SidebarList (which uses manually added children), this component binds to a Data Store to dynamically generate navigation links based on database records.
Use this component to build menus that scale with your data, such as a list of "My Projects," "Recent Customers," or "Active Workspaces."

**1. Data Connection**<br/>
* **Data Store Alias:** You must select a Data Store (CloudDataStore, ReadOnlyDataStore, or DataStore) to populate the list. The component will render one navigation item for each record in the source.

**2. Visual Customization**<br/>
* **Icon Expression:** Instead of a static icon, this property accepts an expression to dynamically determine the icon string for each row. This allows you to visualize data states directly in the menu (e.g., showing a "lock" icon for private projects and a "globe" icon for public ones).

**3. Structure & Children**<br/>
While the list is data-driven, it allows specific children to define the template or add extra functionality.
* **TriggerOnClick:** Essential for defining the navigation logic when a row is clicked (e.g., Navigate to Page 'Project Details' with param ID).
* **IconButton:** For adding secondary actions to each row (e.g., a "Delete" or "Edit" button next to the project name).
* **SidebarNavLink / SidebarNavDropdown:** These allowed types suggest you might be able to mix static and dynamic content or define the render template for the data rows.

**4. Best Use Cases**<br/>
* **Dynamic Menus:** A "Favorites" menu that automatically updates as the user tags items in the app.
* **Category Browsers:** Listing all "Product Categories" from the database as a navigation sidebar.
* **Workspace Switchers:** Showing a list of all teams or organizations the logged-in user belongs to.`,
  };
};

export default SidebarNavListManager;
